/**
 * GTM dataLayer helper - centralised push utility.
 * All custom events flow through here so the schema stays consistent.
 */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type DLEventName =
  | "page_view"
  | "login"
  | "signup"
  | "select_plan"
  | "initiate_checkout"
  | "payment_successful"
  | "submit_investment_form"
  | "download_pdf"
  | "click_run_again"
  | "click_get_started"
  | "click_alerts"
  | "click_history"
  | "toggle_billing_period"
  | "view_results";

export type PageType =
  | "home"
  | "auth"
  | "checkout"
  | "payment_success"
  | "market_news"
  | "alerts"
  | "history"
  | "faq"
  | "privacy"
  | "terms"
  | "reset_password"
  | "seo_admin"
  | "seo_page"
  | "not_found";

export type LoginStatus = "logged_in" | "anonymous";

export interface DLPayload {
  event: DLEventName;
  page_type?: PageType;
  login_status?: LoginStatus;
  plan_type?: string;
  plan_duration?: string;
  plan_price?: number;
  currency?: string;
  investment_amount?: number;
  investment_goal?: string;
  investment_horizon?: string;
  risk_profile?: string;
  auth_method?: string;
  user_id?: string;
  runs_remaining?: number;
  [key: string]: unknown;
}

/* ------------------------------------------------------------------ */
/*  Core push                                                          */
/* ------------------------------------------------------------------ */

export function pushEvent(payload: DLPayload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/* ------------------------------------------------------------------ */
/*  Convenience helpers                                                */
/* ------------------------------------------------------------------ */

export function pushPageView(
  pageType: PageType,
  loginStatus: LoginStatus,
  extra?: Partial<DLPayload>,
) {
  pushEvent({ event: "page_view", page_type: pageType, login_status: loginStatus, ...extra });
}

/** Map pathname → PageType */
export function resolvePageType(pathname: string): PageType {
  if (pathname === "/") return "home";
  if (pathname === "/auth") return "auth";
  if (pathname === "/checkout") return "checkout";
  if (pathname === "/payment-success") return "payment_success";
  if (pathname === "/news") return "market_news";
  if (pathname === "/alerts") return "alerts";
  if (pathname === "/history") return "history";
  if (pathname === "/faq") return "faq";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/terms") return "terms";
  if (pathname === "/reset-password") return "reset_password";
  if (pathname === "/seo-admin") return "seo_admin";
  if (pathname.startsWith("/p/")) return "seo_page";
  return "not_found";
}
